import { token } from '@atlaskit/tokens';
import {
  TagColorPalette,
  TagColorPaletteADSRefresh
} from '@databases/color-picker/palettes';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type SelectOption
} from '@databases/types/database';
import { mapDarkColorToLight } from '@databases/utils/database/field-types/select';
import { getConvertedDatabaseValue } from '@databases/utils/database/utils';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { TIMELINE_BAR_COLORS, type TimelineBarColor } from './view-layout-module.ts';

type TimelineColorField = DatabaseField<DatabaseFieldType.SELECT>;

const TIMELINE_BAR_COLOR_OVERRIDE_PROPERTY_PREFIX =
  '__databases_timeline_bar_color_override__:';
const timelineBarColors = new Set<string>(TIMELINE_BAR_COLORS);

export const getTimelineBarColorOverridePropertyKey = (entryId: string): string =>
  `${TIMELINE_BAR_COLOR_OVERRIDE_PROPERTY_PREFIX}${entryId}`;

export const getTimelineBarColorOverrides = (
  properties: Record<string, unknown> | undefined
): Record<string, TimelineBarColor> => {
  const overrides: Record<string, TimelineBarColor> = {};

  Object.entries(properties ?? {}).forEach(([key, value]) => {
    if (
      !key.startsWith(TIMELINE_BAR_COLOR_OVERRIDE_PROPERTY_PREFIX) ||
      typeof value !== 'string' ||
      !timelineBarColors.has(value)
    ) {
      return;
    }

    const entryId = key.slice(TIMELINE_BAR_COLOR_OVERRIDE_PROPERTY_PREFIX.length);
    if (entryId) {
      overrides[entryId] = value as TimelineBarColor;
    }
  });

  return overrides;
};

export type TimelineItemColors = {
  backgroundColor: string;
  borderColor: string;
  hasCustomColor: boolean;
  textColor: string;
};

export const defaultTimelineItemColors: TimelineItemColors = {
  backgroundColor: token('color.background.accent.gray.subtlest', '#F1F2F4'),
  borderColor: token('color.border', '#DCDFE4'),
  hasCustomColor: false,
  textColor: token('color.text', '#172B4D')
};

const timelineBarOverrideBackgroundColors: Record<TimelineBarColor, string> = {
  gray: token('color.background.accent.gray.subtler', '#DDDEE1'),
  blue: token('color.background.accent.blue.subtler.pressed', '#8FB8F6'),
  teal: token('color.background.accent.teal.subtler.pressed', '#9DD9EE'),
  green: token('color.background.accent.green.subtler.pressed', '#7EE2B8'),
  lime: token('color.background.accent.lime.subtler.pressed', '#B3DF72'),
  yellow: token('color.background.accent.yellow.subtler.pressed', '#EED12B'),
  orange: token('color.background.accent.orange.subtler.pressed', '#FBC828'),
  red: token('color.background.accent.red.subtler.pressed', '#FD9891'),
  magenta: token('color.background.accent.magenta.subtler.pressed', '#F797D2'),
  purple: token('color.background.accent.purple.subtler.pressed', '#D8A0F7')
};

const timelineBarOverrideBorderColors: Record<TimelineBarColor, string> = {
  gray: token('color.border.accent.gray', '#7D818A'),
  blue: token('color.border.accent.blue', '#357DE8'),
  teal: token('color.border.accent.teal', '#2898BD'),
  green: token('color.border.accent.green', '#22A06B'),
  lime: token('color.border.accent.lime', '#6A9A23'),
  yellow: token('color.border.accent.yellow', '#B38600'),
  orange: token('color.border.accent.orange', '#E06C00'),
  red: token('color.border.accent.red', '#E2483D'),
  magenta: token('color.border.accent.magenta', '#CD519D'),
  purple: token('color.border.accent.purple', '#AF59E1')
};

export const getTimelineBarOverrideColors = (
  color: TimelineBarColor
): TimelineItemColors => ({
  backgroundColor: timelineBarOverrideBackgroundColors[color],
  borderColor: timelineBarOverrideBorderColors[color],
  hasCustomColor: true,
  textColor: token('color.text', '#172B4D')
});

const pressedTimelineTagBackgroundColors: Record<SelectOption['color'], string> = {
  blueDark: token('color.background.accent.blue.subtler.pressed', '#8FB8F6'),
  blueLight: token('color.background.accent.blue.subtler.pressed', '#8FB8F6'),
  greenDark: token('color.background.accent.green.subtler.pressed', '#7EE2B8'),
  greenLight: token('color.background.accent.green.subtler.pressed', '#7EE2B8'),
  greyDark: token('color.background.accent.gray.subtler.pressed', '#8C8F97'),
  greyLight: token('color.background.accent.gray.subtler.pressed', '#8C8F97'),
  limeDark: token('color.background.accent.lime.subtler.pressed', '#B3DF72'),
  limeLight: token('color.background.accent.lime.subtler.pressed', '#B3DF72'),
  magentaDark: token('color.background.accent.magenta.subtler.pressed', '#F797D2'),
  magentaLight: token('color.background.accent.magenta.subtler.pressed', '#F797D2'),
  orangeDark: token('color.background.accent.orange.subtler.pressed', '#FBC828'),
  orangeLight: token('color.background.accent.orange.subtler.pressed', '#FBC828'),
  purpleDark: token('color.background.accent.purple.subtler.pressed', '#D8A0F7'),
  purpleLight: token('color.background.accent.purple.subtler.pressed', '#D8A0F7'),
  redDark: token('color.background.accent.red.subtler.pressed', '#FD9891'),
  redLight: token('color.background.accent.red.subtler.pressed', '#FD9891'),
  tealDark: token('color.background.accent.teal.subtler.pressed', '#9DD9EE'),
  tealLight: token('color.background.accent.teal.subtler.pressed', '#9DD9EE'),
  yellowDark: token('color.background.accent.yellow.subtler.pressed', '#EED12B'),
  yellowLight: token('color.background.accent.yellow.subtler.pressed', '#EED12B')
};

const timelineTagBorderColors: Record<SelectOption['color'], string> = {
  blueDark: token('color.border.accent.blue', '#357DE8'),
  blueLight: token('color.border.accent.blue', '#357DE8'),
  greenDark: token('color.border.accent.green', '#22A06B'),
  greenLight: token('color.border.accent.green', '#22A06B'),
  greyDark: token('color.border.accent.gray', '#7D818A'),
  greyLight: token('color.border.accent.gray', '#7D818A'),
  limeDark: token('color.border.accent.lime', '#6A9A23'),
  limeLight: token('color.border.accent.lime', '#6A9A23'),
  magentaDark: token('color.border.accent.magenta', '#CD519D'),
  magentaLight: token('color.border.accent.magenta', '#CD519D'),
  orangeDark: token('color.border.accent.orange', '#E06C00'),
  orangeLight: token('color.border.accent.orange', '#E06C00'),
  purpleDark: token('color.border.accent.purple', '#AF59E1'),
  purpleLight: token('color.border.accent.purple', '#AF59E1'),
  redDark: token('color.border.accent.red', '#E2483D'),
  redLight: token('color.border.accent.red', '#E2483D'),
  tealDark: token('color.border.accent.teal', '#2898BD'),
  tealLight: token('color.border.accent.teal', '#2898BD'),
  yellowDark: token('color.border.accent.yellow', '#B38600'),
  yellowLight: token('color.border.accent.yellow', '#B38600')
};

const getTimelineTagColors = (color: SelectOption['color']) => {
  const useDarkToLightMapping = isFeatureFlagEnabled(
    'platformLozengeTagBadgeVisualUplifts'
  );
  const tagColor = useDarkToLightMapping ? mapDarkColorToLight(color) : color;
  const palette = useDarkToLightMapping
    ? TagColorPalette
    : isFeatureFlagEnabled('platform-component-visual-refresh')
      ? TagColorPaletteADSRefresh
      : TagColorPalette;
  const backgroundColor = pressedTimelineTagBackgroundColors[tagColor];

  return {
    backgroundColor,
    borderColor: timelineTagBorderColors[tagColor],
    textColor: palette.textColor(tagColor)
  };
};

const getHexFromCssColor = (color: string) => {
  const hex = color.match(/#[0-9a-fA-F]{6}/)?.[0];
  if (hex) {
    return hex;
  }

  const shortHex = color.match(/#[0-9a-fA-F]{3}\b/)?.[0];
  if (!shortHex) {
    return undefined;
  }

  return `#${shortHex
    .slice(1)
    .split('')
    .map((character) => `${character}${character}`)
    .join('')}`;
};

const getRelativeLuminance = (hexColor: string) => {
  const [red, green, blue] = [1, 3, 5].map((startIndex) => {
    const value = parseInt(hexColor.slice(startIndex, startIndex + 2), 16) / 255;

    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

export const getContrastingTextColor = (
  backgroundColor: string,
  fallbackTextColor: string
) => {
  const hexColor = getHexFromCssColor(backgroundColor);
  if (!hexColor) {
    return fallbackTextColor;
  }

  return getRelativeLuminance(hexColor) < 0.45
    ? token('color.text.inverse', '#FFFFFF')
    : token('color.text', '#172B4D');
};

type GetTimelineItemColorDataOptions = {
  colorField?: TimelineColorField;
  database: Database;
  entry: DatabaseEntry;
  state: Parameters<typeof getConvertedDatabaseValue>[3];
};

export const getTimelineItemColors = ({
  colorField,
  database,
  entry,
  state
}: GetTimelineItemColorDataOptions): TimelineItemColors => {
  if (!colorField) {
    return defaultTimelineItemColors;
  }

  const value = getConvertedDatabaseValue(database, colorField, entry, state);
  if (value?.type !== DatabaseFieldType.SELECT || !value.value.length) {
    return defaultTimelineItemColors;
  }

  const option = colorField.settings.selectOptions.find(
    (selectOption) => value.value[0] === (selectOption.id ?? selectOption.label)
  );

  if (!option) {
    return defaultTimelineItemColors;
  }

  return {
    ...getTimelineTagColors(option.color),
    hasCustomColor: true
  };
};
